import React from 'react';
import { motion } from 'framer-motion';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { AlertTriangle } from 'lucide-react';

// Register required Chart.js components
ChartJS.register(ArcElement, Tooltip);

interface Dimension {
  name: string;
  summary: string;
  selfScore: number;
  evaluatorScore: number;
  feedback: string[];
  specificExamples: string;
  actionableRecommendations: string[];
  tag?: string;
}

interface DimensionDetailsProps {
  dimensions: Dimension[];
}

export function DimensionDetails({ dimensions }: DimensionDetailsProps) {
  if (!dimensions || dimensions.length === 0) {
    return (
      <div className="px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-50 rounded-xl p-8 text-center"
        >
          <div className="flex justify-center mb-4">
            <AlertTriangle className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Detailed Analysis Available
          </h3>
          <p className="text-gray-600 max-w-lg mx-auto">
            The detailed skills analysis is not available yet. This section will be populated once all evaluations are completed and the report is generated.
          </p>
        </motion.div>
      </div>
    );
  }

  const getTagColor = (tag?: string) => {
    switch (tag) {
      case 'Key Strength':
        return 'bg-green-100 text-green-800';
      case 'Hidden Strength':
        return 'bg-blue-100 text-blue-800';
      case 'Blind Spot':
        return 'bg-amber-100 text-amber-800';
      case 'Confirmed Strength':
        return 'bg-purple-100 text-purple-800';
      default:
        return '';
    }
  };

  const getChartData = (selfScore: number, evaluatorScore: number) => ({
    labels: ['Self Assessment', 'Evaluator Average'],
    datasets: [
      {
        label: 'Self Assessment',
        data: [selfScore, 5 - selfScore],
        backgroundColor: [
          'rgba(238, 153, 234, 0.8)',
          'rgba(238, 153, 234, 0.1)'
        ],
        borderColor: [
          'rgb(238, 153, 234)',
          'transparent'
        ],
        borderWidth: 1,
        weight: 1,
        spacing: 0
      },
      {
        label: 'Evaluator Average',
        data: [evaluatorScore, 5 - evaluatorScore],
        backgroundColor: [
          'rgba(88, 211, 226, 0.8)',
          'rgba(88, 211, 226, 0.1)'
        ],
        borderColor: [
          'rgb(88, 211, 226)',
          'transparent'
        ],
        borderWidth: 1,
        weight: 1,
        spacing: 0
      }
    ]
  });

  const chartOptions = {
    responsive: true,
    cutout: '60%',
    layout: {
      padding: {
        top: 30,
        bottom: 20,
        left: 20,
        right: 20
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          font: {
            family: 'Inter, system-ui, sans-serif',
            size: 12
          },
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          generateLabels: (chart: any) => {
            const datasets = chart.data.datasets;
            return datasets.map((dataset: any, i: number) => ({
              text: dataset.label,
              fillStyle: dataset.backgroundColor[0],
              strokeStyle: dataset.borderColor[0],
              lineWidth: 0,
              hidden: !chart.isDatasetVisible(i),
              index: i
            }));
          }
        }
      },
      tooltip: {
        enabled: true,
        position: 'nearest',
        yAlign: 'bottom',
        xAlign: 'center',
        caretPadding: 12,
        caretSize: 8,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        bodyColor: '#4b5563',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        boxPadding: 6,
        usePointStyle: true,
        z: 9999,
        titleFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          family: 'Inter, system-ui, sans-serif',
          size: 13
        },
        callbacks: {
          title: function(tooltipItems: any[]) {
            return tooltipItems[0].dataset.label;
          },
          label: function(context: any) {
            if (context.dataIndex === 1) return null;
            
            const score = context.raw.toFixed(1);
            const percentage = ((context.raw / 5) * 100).toFixed(0);
            const label = context.dataset.label;
            
            return [
              `Score: ${score} / 5`,
              `Percentage: ${percentage}%`,
              label === 'Self Assessment' 
                ? 'Your self-evaluated score'
                : 'Average score from evaluators'
            ];
          },
          afterLabel: function(context: any) {
            if (context.dataIndex === 1) return null;
            
            if (context.dataset.label === 'Evaluator Average') {
              const selfScore = context.chart.data.datasets[0].data[0];
              const evalScore = context.raw;
              const diff = (evalScore - selfScore).toFixed(1);
              const diffText = diff > 0 
                ? `${diff} points higher than your assessment`
                : diff < 0
                ? `${Math.abs(Number(diff))} points lower than your assessment`
                : 'Matches your self-assessment';
              return [`Comparison: ${diffText}`];
            }
            return null;
          }
        }
      }
    },
    hover: {
      mode: 'nearest',
      intersect: true
    },
    events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
    onHover: (event: any, elements: any[]) => {
      const chartElement = event.native?.target;
      if (chartElement) {
        chartElement.style.cursor = elements.length ? 'pointer' : 'default';
      }
    }
  };

  return (
    <div className="px-6 py-8 space-y-12">
      {dimensions?.map((dimension, index) => (
        <motion.div
          key={dimension.name}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="border-b border-gray-200 pb-8 last:border-b-0"
        >
          <div className="flex items-center space-x-3 mb-4">
            <h3 className="text-xl font-bold text-gray-900">{dimension.name}</h3>
            {dimension.tag && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTagColor(dimension.tag)}`}>
                {dimension.tag}
              </span>
            )}
          </div>
          
          <p className="text-gray-600 mb-6">{dimension.summary}</p>
          
          <div className="relative h-[360px] mb-6">
            <div className="absolute inset-0 z-10">
              <Doughnut 
                data={getChartData(dimension.selfScore, dimension.evaluatorScore)} 
                options={chartOptions}
              />
            </div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[5]">
              <div className="text-center">
                <div className="text-3xl font-bold text-secondary">
                  {dimension.evaluatorScore.toFixed(1)}
                </div>
                <div className="text-sm font-medium text-gray-500">Evaluators' Score</div>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            {/* Detailed Feedback Section */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Detailed Feedback</h4>
              <ul className="space-y-3">
                {dimension.feedback.map((point, i) => (
                  <li key={i} className="flex items-start space-x-3 text-sm text-gray-600">
                    <span className="text-primary mt-1">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Specific Examples Section */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Specific Examples</h4>
              <div className="text-sm text-gray-600">
                {dimension.specificExamples}
              </div>
            </div>

            {/* Actionable Recommendations Section */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Actionable Recommendations</h4>
              <ul className="space-y-3">
                {dimension.actionableRecommendations.map((recommendation, i) => (
                  <li key={i} className="flex items-start space-x-3 text-sm text-gray-600">
                    <span className="text-secondary mt-1">•</span>
                    <span>{recommendation}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}